import type { ReactNode } from 'react';
import { ChevronRight } from '../../components/ui/icons';
import { Switch } from '../../components/ui/Switch';

export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-2 pb-[9px] pt-[22px] text-[11px] font-extrabold uppercase tracking-[0.08em] text-ink3">
      {children}
    </div>
  );
}

export function Group({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-[20px] border border-black/5 bg-card shadow-tile">{children}</div>
  );
}

export function Divider() {
  return <div className="ml-[58px] h-px bg-black/[0.06]" />;
}

function IconTile({ node, bg }: { node: ReactNode; bg?: string }) {
  return (
    <span
      className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[9px]"
      style={bg ? { background: bg } : undefined}
    >
      {node}
    </span>
  );
}

export function Row({
  icon,
  iconBg,
  label,
  children
}: {
  icon: ReactNode;
  iconBg: string;
  label: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-[13px] px-[15px] py-[12px]">
      <IconTile node={icon} bg={iconBg} />
      <span className="flex-1 text-[15px] font-semibold tracking-[-0.01em] text-ink">{label}</span>
      <span className="ml-auto min-w-0 text-right">{children}</span>
    </div>
  );
}

export function ActionRow({
  icon,
  iconNode,
  iconBg,
  label,
  value,
  trailing,
  onClick
}: {
  icon?: ReactNode;
  iconNode?: ReactNode;
  iconBg?: string;
  label: string;
  value?: string;
  trailing?: ReactNode;
  onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className="tap flex w-full items-center gap-[13px] px-[15px] py-[12px] text-left">
      {iconNode ?? <IconTile node={icon} bg={iconBg} />}
      <span className="flex-1 text-[15px] font-semibold tracking-[-0.01em] text-ink">{label}</span>
      {value && <span className="text-[14px] font-medium text-ink2">{value}</span>}
      {trailing ?? <ChevronRight width={18} height={18} strokeWidth={2.4} className="text-ink4" />}
    </button>
  );
}

export function ToggleRow({
  icon,
  iconBg,
  label,
  sub,
  checked,
  onChange
}: {
  icon: ReactNode;
  iconBg: string;
  label: string;
  sub?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-[13px] px-[15px] py-[12px]">
      <IconTile node={icon} bg={iconBg} />
      <div className="flex-1">
        <div className="text-[15px] font-semibold tracking-[-0.01em] text-ink">{label}</div>
        {sub && <div className="mt-[1px] text-[12px] font-medium text-ink3">{sub}</div>}
      </div>
      <Switch checked={checked} onChange={onChange} label={label} />
    </div>
  );
}
